import React, { useEffect, useRef, useState } from 'react';

import { DesktopDatePicker, LocalizationProvider } from '@material-ui/pickers';
import DateFnsUtils from '@material-ui/pickers/adapter/date-fns';
import { format, isValid, parse as parseDate } from 'date-fns';
import deLocale from 'date-fns/locale/de';
import PropTypes from 'prop-types';

import { ThemedTextField } from 'components/ThemedTextField/ThemedTextField';

import './InputField.sass';

const DATE_FORMAT = 'dd.MM.yyyy';
const PICKER_POPPER_SELECTOR = '.MuiPickersPopper-root';

const parseDateValue = (value) => {
  if (!value) {
    return null;
  }

  const parsedDate = parseDate(String(value), DATE_FORMAT, new Date());
  return isValid(parsedDate) ? parsedDate : null;
};

const getClassName = ({ error, isEdited, oldValue, value }) => {
  if (error) return 'input-field__error';
  if (isEdited || (oldValue && oldValue !== value)) return 'input-field__edited';
  return 'input-field__normal';
};

const isInsidePicker = (target) => Boolean(target?.closest?.(PICKER_POPPER_SELECTOR));

export const FormDateField = ({
  name,
  label,
  value,
  onChange,
  error,
  helperText,
  disabled,
  readOnly,
  infoText,
  oldValue,
  fullWidth,
  isEdited,
  testId,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      const { target } = event;

      if (isInsidePicker(target)) {
        return;
      }

      if (containerRef.current?.contains(target)) {
        return;
      }

      setOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [open]);

  if (readOnly || disabled) {
    return (
      <div className={`input-field ${fullWidth ? 'input-field__full-width' : ''}`}>
        <ThemedTextField
          fullWidth={fullWidth}
          label={label}
          name={name}
          value={value ?? ''}
          disabled
          error={error}
          helperText={helperText}
          variant="standard"
          className={getClassName({ error, isEdited, oldValue, value })}
          data-testid={testId}
        />
        {infoText && (
          <div className="input-field__info-text input-field__info-text__disabled">{infoText}</div>
        )}
      </div>
    );
  }

  const parsedDate = parseDateValue(value);

  // Deliberately does not close the picker: onChange also fires for partial
  // selections (e.g. picking a year in the year view). Closing is handled by
  // the picker itself via onAccept/onClose once a day is selected.
  const handleChange = (newDate) => {
    if (newDate && isValid(newDate)) {
      onChange(name, format(newDate, DATE_FORMAT));
      return;
    }

    if (newDate === null) {
      onChange(name, '');
    }
  };

  const handleOpenPickerButtonClick = (event) => {
    if (!open) {
      return;
    }

    event.stopPropagation();
    event.preventDefault();
    setOpen(false);
  };

  return (
    <LocalizationProvider dateAdapter={DateFnsUtils} locale={deLocale}>
      <DesktopDatePicker
        inputFormat={DATE_FORMAT}
        mask="__.__.____"
        value={parsedDate}
        disabled={disabled}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onAccept={() => setOpen(false)}
        disableCloseOnSelect={false}
        onChange={handleChange}
        OpenPickerButtonProps={{
          onClick: handleOpenPickerButtonClick,
        }}
        PopperProps={{
          style: { zIndex: 10001 },
        }}
        renderInput={(params) => (
          <div
            ref={containerRef}
            className={`input-field input-field--date-picker ${
              fullWidth ? 'input-field__full-width' : ''
            }`}>
            <ThemedTextField
              {...params}
              fullWidth={fullWidth}
              label={label}
              name={name}
              error={error}
              helperText={helperText}
              variant="standard"
              className={getClassName({ error, isEdited, oldValue, value })}
              data-testid={testId}
              onClick={(event) => {
                if (open) {
                  setOpen(false);
                  return;
                }

                params.onClick?.(event);
              }}
            />
            {infoText && <div className="input-field__info-text">{infoText}</div>}
          </div>
        )}
      />
    </LocalizationProvider>
  );
};

FormDateField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  infoText: PropTypes.string,
  oldValue: PropTypes.string,
  fullWidth: PropTypes.bool,
  isEdited: PropTypes.bool,
  testId: PropTypes.string,
};

FormDateField.defaultProps = {
  label: '',
  value: '',
  error: false,
  helperText: '',
  disabled: false,
  readOnly: false,
  infoText: '',
  oldValue: '',
  fullWidth: true,
  isEdited: false,
  testId: '',
};
